/**
 * The right Details panel's frame, shared by the room editor's SidePanel and
 * the assembler's panel: the header action row (undo / redo / Save, or the
 * View-only badge), the scrollable body (which tells every field widget
 * whether the panel is read-only, so each shows its value as plain text), and
 * the always-visible hint footer. The Kai v2 light panel language: white surface, bone hairlines,
 * ink structure, mono-uppercase labels.
 */

import { css } from '@emotion/react';
import { bone, ink, lemon, midtone, monoLabel, white } from '../../styles';
import { HINT_STYLE, ICON_BUTTON_STYLE } from './chrome';
import { PanelReadOnlyContext } from './panelReadOnly';

const PANEL_ROOT_STYLE = css`
  label: PANEL_ROOT_STYLE;
  width: 322px;
  flex: 0 0 322px;
  display: flex; flex-direction: column;
  background: ${white};
  color: ${ink};
  border-left: 1px solid ${bone};
  font-size: 14px; line-height: 1.4;
  input[type="checkbox"] { accent-color: ${ink}; width: 15px; height: 15px; }
`;

/** Panel header (the right-aligned undo/redo + Save actions). */
const PANEL_TITLE_STYLE = css`
  label: PANEL_TITLE_STYLE;
  ${monoLabel}
  flex: none;
  display: flex; align-items: center; justify-content: flex-end;
  padding: 12px 18px;
  background: ${white};
  border-bottom: 1px solid ${bone};
  color: ${midtone};
  font-size: 11px; letter-spacing: 0.14em;
`;

/** The scrollable middle: figures + the flooring rows / selection inspector. */
const PANEL_BODY_STYLE = css`
  label: PANEL_BODY_STYLE;
  flex: 1; min-height: 0; overflow-y: auto;
  padding: 16px 18px 22px;
`;

/**
 * Contextual how-to hint, pinned below the scroll area so it is ALWAYS
 * visible: inline hints scrolled off screen once something was selected, and
 * users never saw them.
 */
const HINT_FOOTER_STYLE = css`
  label: HINT_FOOTER_STYLE;
  flex: none;
  border-top: 1px solid ${bone};
  padding: 10px 18px;
  background: ${white};
`;

/** The header's right-side action group, flex-centered so the undo/redo
 *  icons and the Save button share one vertical axis (baseline alignment of
 *  mixed inline buttons puts them at different heights). */
const PANEL_HEADER_ACTIONS_STYLE = css`
  label: PANEL_HEADER_ACTIONS_STYLE;
  display: inline-flex; align-items: center;
`;

/** Panel-header Save button: lemon when there's something to save. */
const SAVE_BTN_STYLE = css`
  label: SAVE_BTN_STYLE;
  ${monoLabel}
  height: 33px; padding: 0 12px; margin-left: 10px;
  font-size: 10.5px; cursor: pointer; white-space: nowrap;
  border: 1px solid ${ink}; background: ${lemon}; color: ${ink};
  transition: filter .12s, opacity .12s;
  &:hover:not(:disabled) { filter: brightness(0.96); }
  &:disabled {
    background: ${white}; color: ${midtone}; border-color: ${bone};
    opacity: 0.6; cursor: default;
  }
`;

/** The panel header's undo and redo: small square icon buttons. */
const HEADER_ICON_BUTTON_STYLE = css`
  label: HEADER_ICON_BUTTON_STYLE;
  width: 33px; height: 33px;
  & + & { margin-left: 6px; }
`;

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iP/.test(navigator.platform);

/**
 * Traditional undo mark: a ¾-circle arc (counterclockwise, left → bottom →
 * right → top) whose end continues tangentially into the triangle head. The
 * head points out through the top-left gap.
 */
function UndoIcon(): React.JSX.Element {
  return (
    <svg viewBox='0 0 16 16' width='19' height='19' aria-hidden='true'>
      <path d='M3.25 8 A4.75 4.75 0 1 0 8 3.25' fill='none' stroke='currentColor' strokeWidth='1.8' />
      <path d='M3.21 3.25 L8.15 0.69 L8.15 5.81 Z' fill='currentColor' />
    </svg>
  );
}

/** The mirror of UndoIcon (clockwise; head out through the top-right gap). */
function RedoIcon(): React.JSX.Element {
  return (
    <svg viewBox='0 0 16 16' width='19' height='19' aria-hidden='true'>
      <path d='M12.75 8 A4.75 4.75 0 1 1 8 3.25' fill='none' stroke='currentColor' strokeWidth='1.8' />
      <path d='M12.79 3.25 L7.85 0.69 L7.85 5.81 Z' fill='currentColor' />
    </svg>
  );
}

export interface PanelShellProps {
  readOnly: boolean;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  /** Host save hook: when present, the header shows the Save button. */
  onSave?: () => void;
  saveDisabled: boolean;
  saveLabel: string;
  /** Tooltip for the Save button, in both its states. */
  saveTitle: string;
  /** The one-line contextual how-to hint for the footer. */
  hint: string;
  /** Dev/test seam on the root. */
  testId?: string;
  children: React.ReactNode;
}

export function PanelShell(
  { readOnly, canUndo, canRedo, onUndo, onRedo, onSave, saveDisabled, saveLabel, saveTitle, hint, testId, children }: PanelShellProps,
): React.JSX.Element {
  return (
    <div css={PANEL_ROOT_STYLE} data-testid={testId}>
      <div css={PANEL_TITLE_STYLE} data-testid='panel-title'>
        {readOnly ? <span data-testid='read-only-badge'>View only</span> : (
          <span css={PANEL_HEADER_ACTIONS_STYLE}>
            <button
              css={[ICON_BUTTON_STYLE, HEADER_ICON_BUTTON_STYLE]}
              data-act='undo'
              aria-label='Undo'
              title={`Undo (${IS_MAC ? '⌘Z' : 'Ctrl+Z'})`}
              disabled={!canUndo}
              onClick={onUndo}
            >
              <UndoIcon />
            </button>
            <button
              css={[ICON_BUTTON_STYLE, HEADER_ICON_BUTTON_STYLE]}
              data-act='redo'
              aria-label='Redo'
              title={`Redo (${IS_MAC ? '⇧⌘Z' : 'Ctrl+Y'})`}
              disabled={!canRedo}
              onClick={onRedo}
            >
              <RedoIcon />
            </button>
            {onSave && (
              <button css={SAVE_BTN_STYLE} data-act='save' disabled={saveDisabled} title={saveTitle} onClick={onSave}>
                {saveLabel}
              </button>
            )}
          </span>
        )}
      </div>
      <div css={PANEL_BODY_STYLE}>
        <PanelReadOnlyContext.Provider value={readOnly}>{children}</PanelReadOnlyContext.Provider>
      </div>
      <div css={HINT_FOOTER_STYLE} data-testid='selection-hint'>
        <div css={HINT_STYLE}>{hint}</div>
      </div>
    </div>
  );
}
